import React, { useEffect, useState } from "react";
import Typography from "@mui/material/Typography";
import {
  Accordion,
  AccordionDetails,
  AccordionSummaryReverse,
} from "./Accordion";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Link } from "react-router-dom";
import Box from "@mui/material/Box";
import LinkIcon from "@mui/icons-material/Link";

const createAlertslink = (d) => {
  if (d.latitude !== 0 && d.longitude !== 0) {
    let url = `/map?id=${d.incidentId}&lat=${d.latitude}&lng=${d.longitude}&type=`;
    if (d.text.includes("incident")) {
      url = url + "Incidents";
    } else if (d.text.includes("congestion")) {
      url = url + "Congestion";
    }
    if (null !== d.mapName) {
      url = url + `&mapName=${d.mapName}`;
    }
    d.link = url;
  }
  return d;
};

const getBoundsFromStorage = () => {
  const north = parseFloat(localStorage.getItem("mapBoundsNorth"));
  const south = parseFloat(localStorage.getItem("mapBoundsSouth"));
  const east = parseFloat(localStorage.getItem("mapBoundsEast"));
  const west = parseFloat(localStorage.getItem("mapBoundsWest"));
  if (isNaN(north) || isNaN(south) || isNaN(east) || isNaN(west)) return null;
  return { north, south, east, west };
};

const hasLatLng = (alert) =>
  alert.latitude &&
  alert.longitude &&
  (parseFloat(alert.latitude) !== 0 || parseFloat(alert.longitude) !== 0);

const isInBounds = (alert, bounds) => {
  if (!hasLatLng(alert)) return true;
  if (!bounds) return true;
  const lat = parseFloat(alert.latitude);
  const lng = parseFloat(alert.longitude);
  return (
    lat >= bounds.south &&
    lat <= bounds.north &&
    lng >= bounds.west &&
    lng <= bounds.east
  );
};

const AlertRow = ({ alert, idx }) => (
  <Box className={idx % 2 === 0 ? "styledtablerow" : ""} sx={{ p: 1 }}>
    <Typography variant="body2">
      <span dangerouslySetInnerHTML={{ __html: alert.text }}></span>
      {undefined !== alert.link && (
        <span>
          &nbsp;(
          <Link
            to={alert.link}
            target="_blank"
            className="motdLink"
            title="Click for incident map"
          >
            Location
            <LinkIcon fontSize="small" className="link-icon" />
          </Link>
          )
        </span>
      )}
    </Typography>
  </Box>
);

export default function Alerts(props) {
  const [alerts, setAlerts] = React.useState([]);
  const [update, setUpdate] = useState(null);
  const [mapBounds, setMapBounds] = useState(getBoundsFromStorage);
  const [showOutOfView, setShowOutOfView] = useState(false);
  const updateDelay = 30;

  useEffect(() => {
    const id = setInterval(() => {
      setUpdate(true);
    }, updateDelay * 1000);
    return () => clearInterval(id);
  }, [updateDelay]);

  useEffect(() => {
    const handleBoundsChange = (e) => {
      setMapBounds(e.detail || getBoundsFromStorage());
      setShowOutOfView(false);
    };
    window.addEventListener("mapBoundsChange", handleBoundsChange);
    return () =>
      window.removeEventListener("mapBoundsChange", handleBoundsChange);
  }, []);

  useEffect(() => {
    const fetchApi = async () => {
      const res = await fetch(process.env.REACT_APP_API_HOST + "/alerts.json");
      res.json().then((res) => {
        const decodedAlerts = res.map((alert) => createAlertslink(alert));
        setAlerts(decodedAlerts);
        setUpdate(false);
      });
    };
    fetchApi().catch(() => {});
  }, [update]);

  if (alerts.length > 0) {
    const inViewAlerts = alerts.filter((a) => isInBounds(a, mapBounds));
    const outOfViewAlerts = alerts.filter((a) => !isInBounds(a, mapBounds));
    const outOfViewPlural = outOfViewAlerts.length !== 1 ? "s" : "";
    const outOfViewToggleText = showOutOfView
      ? `Hide alert${outOfViewPlural} outside current view`
      : `View remaining alert${outOfViewPlural} outside current view`;

    return (
      <Accordion>
        <AccordionSummaryReverse
          expandIcon={<ExpandMoreIcon />}
          aria-controls="alert-content"
          id="alert-header"
          className="controls-header"
        >
          <img
            src={process.env.PUBLIC_URL + "/mapicons/Alerts_v14.png"}
            alt="Alert"
            width={25}
            height={25}
          />
          <Typography variant="subtitle3" sx={{ fontWeight: "bold", ml: 1 }}>
            Alerts ({alerts.length})
          </Typography>
        </AccordionSummaryReverse>
        <AccordionDetails
          sx={{
            overflow: "auto",
            padding: 0,
            borderBottom: "1px solid rgb(192,192,192,0.3)",
          }}
        >
          {inViewAlerts.map((alert, idx) => (
            <AlertRow alert={alert} idx={idx} key={idx} />
          ))}
          {outOfViewAlerts.length > 0 && (
            <>
              <Box sx={{ p: 1 }}>
                <Typography
                  variant="body2"
                  component="span"
                  onClick={() => setShowOutOfView((v) => !v)}
                  sx={{
                    cursor: "pointer",
                    color: "primary.main",
                    userSelect: "none",
                  }}
                >
                  {outOfViewToggleText}
                </Typography>
              </Box>
              {showOutOfView &&
                outOfViewAlerts.map((alert, idx) => (
                  <AlertRow
                    alert={alert}
                    idx={inViewAlerts.length + idx}
                    key={`ov-${idx}`}
                  />
                ))}
            </>
          )}
        </AccordionDetails>
      </Accordion>
    );
  } else {
    return null;
  }
}
